/* Divyne design system: behaviour for the design-system pages (branch design-system-pdp, 19 Oct 2026). Web components over
   server-rendered markup; every element works as plain HTML first. Mirrors the design system's components/bundle.js. */
(() => {
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Dates: Bangkok is UTC+7 all year. The words come from the page (the cart section's settings, so they translate);
     a missing one falls back to the Thai below. ---- */
  const DAY = 864e5;
  const WORDS = {
    daysShort: 'อา.,จ.,อ.,พ.,พฤ.,ศ.,ส.', daysLong: 'วันอาทิตย์,วันจันทร์,วันอังคาร,วันพุธ,วันพฤหัสบดี,วันศุกร์,วันเสาร์',
    months: 'ม.ค.,ก.พ.,มี.ค.,เม.ย.,พ.ค.,มิ.ย.,ก.ค.,ส.ค.,ก.ย.,ต.ค.,พ.ย.,ธ.ค.', orderToday: 'สั่งก่อนเที่ยง ส่งวันนี้',
    shipsToday: 'ส่งวันนี้', shipsTomorrow: 'ส่งพรุ่งนี้', shipsDay: 'ส่ง[day]', arrivesTomorrow: 'ถึงพรุ่งนี้',
    arrivesDate: 'ถึง [day] [date] [month]'
  };
  const words = (el) => {
    const w = {};
    Object.keys(WORDS).forEach((k) => { w[k] = (el && el.dataset['w' + k[0].toUpperCase() + k.slice(1)]) || WORDS[k]; });
    ['daysShort', 'daysLong', 'months'].forEach((k) => { w[k] = w[k].split(',').map((s) => s.trim()); });
    return w;
  };
  const bkkDay = (ms) => { const d = new Date(ms + 7 * 36e5); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); };

  // The real dispatch rules (Jamie, 19 Oct 2026): Monday to Saturday, cut-off at noon Bangkok time, no Sunday dispatch;
  // Bangkok and nearby provinces the next day. Same maths as the design system's DeliveryLine.
  function dispatchInfo(o, now, w) {
    const cutoff = o.cutoffHour == null ? 12 : o.cutoffHour, ship = o.shipDays || [1, 2, 3, 4, 5, 6], holidays = o.holidays || [];
    const near = o.daysNear || 1;
    const today = bkkDay(now), hour = new Date(now + 7 * 36e5).getUTCHours();
    const ships = (t) => ship.indexOf(new Date(t).getUTCDay()) >= 0 && holidays.indexOf(new Date(t).toISOString().slice(0, 10)) < 0;
    let dispatch = today;
    if (!(ships(today) && hour < cutoff)) { dispatch = today + DAY; while (!ships(dispatch)) dispatch += DAY; }
    let arrive = dispatch + near * DAY;
    if (o.deliverSunday === false && new Date(arrive).getUTCDay() === 0) arrive += DAY;
    const d = new Date(arrive);
    return {
      today: dispatch === today,
      cutoffAt: dispatch + cutoff * 36e5 - 7 * 36e5,
      send: dispatch === today ? w.orderToday
        : dispatch === today + DAY ? w.shipsTomorrow : w.shipsDay.replace('[day]', w.daysLong[new Date(dispatch).getUTCDay()]),
      head: dispatch === today ? w.shipsToday
        : dispatch === today + DAY ? w.shipsTomorrow : w.shipsDay.replace('[day]', w.daysLong[new Date(dispatch).getUTCDay()]),
      when: arrive === today + DAY ? w.arrivesTomorrow
        : w.arrivesDate.replace('[day]', w.daysShort[d.getUTCDay()]).replace('[date]', d.getUTCDate()).replace('[month]', w.months[d.getUTCMonth()])
    };
  }

  // A real end date: "ถึง 31 ต.ค." until the last three days, then "เหลือ 3 วัน" … "วันสุดท้าย". No ticking clock. Once the
  // date has passed it goes, with the bar's " · " before it: never a past date or a stuck "วันสุดท้าย" (3 Nov 2026).
  define('dv-until', class extends HTMLElement {
    connectedCallback() {
      const d = Math.ceil((Date.parse(this.dataset.ends) - Date.now()) / DAY);
      if (d < 1) (this.closest('.dv-bar-end') || this).hidden = true;
      else if (d === 1) this.textContent = this.dataset.last;
      else if (d <= 3) this.textContent = (this.dataset.left || '').replace('[n]', d);
    }
  });

  /* ---- The campaign's real end (ends_at; Jamie, 3 Nov 2026): a cached page can outlive it, so whatever the deals put on
     the page goes when it passes. [data-dv-hide-at] hides, [data-dv-show-at] shows (the bar between offers),
     [data-dv-swap-at] takes its data-dv-after words, and a <dv-tiers data-hide-at> drops its deal packs. ---- */
  const expire = () => {
    const now = Date.now();
    let next = Infinity;
    const due = (el, attr) => {
      const at = Date.parse(el.getAttribute(attr));
      if (!(at > 0)) return false;
      if (at > now) { next = Math.min(next, at); return false; }
      return true;
    };
    document.querySelectorAll('[data-dv-hide-at]').forEach((el) => { if (due(el, 'data-dv-hide-at')) el.hidden = true; });
    document.querySelectorAll('[data-dv-show-at]').forEach((el) => { if (due(el, 'data-dv-show-at')) el.hidden = false; });
    document.querySelectorAll('[data-dv-swap-at]').forEach((el) => { if (due(el, 'data-dv-swap-at')) { el.textContent = el.dataset.dvAfter; el.removeAttribute('data-dv-swap-at'); } });
    document.querySelectorAll('dv-tiers[data-hide-at]').forEach((el) => { if (due(el, 'data-hide-at') && el.dropDeals) el.dropDeals(); });
    if (next - now < 2 ** 31) setTimeout(expire, next - now + 1000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', expire); else expire();

  // The campaign bar on a single with the deals opens its course sheet; elsewhere it is a link to the singles.
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-dv-sheet-open]');
    const sheet = open && document.querySelector('dialog[data-dv-sheet]');
    if (sheet && sheet.showModal && !sheet.open) { e.preventDefault(); sheet.showModal(); }
  });

  /* ---- CourseTiers: the switch and its summary. Tells the buy box which variant, quantity and total she chose. ---- */
  const GLYPHS = {
    jar: '<rect x="2" y="1" width="14" height="5" rx="1.5"/><rect x="1" y="7" width="16" height="12" rx="3"/>',
    bottle: '<rect x="6" y="1" width="6" height="4" rx="1"/><rect x="4" y="6" width="10" height="13" rx="2.5"/>',
    tube: '<path d="M3 1h12l-2 13H5z"/><rect x="5.5" y="15" width="7" height="4" rx="1"/>',
    box: '<rect x="1" y="5" width="16" height="14" rx="2"/><rect x="0" y="2" width="18" height="4" rx="1.5"/>'
  };
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  define('dv-tiers', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => { if (e.target.matches('input[type="radio"]')) this.show(e.target, true); });
      // The nudge (one pack → the first deal) chooses that deal for her, the same as tapping it on the switch.
      this.addEventListener('click', (e) => {
        const n = e.target.closest('[data-dv-nudge]');
        const r = n && this.radios()[+n.dataset.dvNudge];
        if (r) { r.checked = true; this.show(r, true); }
      });
    }
    radios() { return [...this.querySelectorAll('input[type="radio"]')]; }
    // After the deals' end: only the packs without a deal stay, and she is back on one pack.
    dropDeals() {
      this.querySelectorAll('.dv-seg-opt[data-dv-deal], [data-dv-nudge]').forEach((o) => o.remove());
      const seg = this.querySelector('.dv-seg'), left = this.radios();
      if (seg) { seg.style.setProperty('--dv-n', left.length); seg.hidden = left.length < 2; }
      this.removeAttribute('data-hide-at');
      if (left[0]) { left[0].checked = true; this.show(left[0], true); }
    }
    setIndex(i) { const r = this.radios()[i]; if (r && !r.checked) { r.checked = true; this.show(r, false); } }
    show(input, announce) {
      this.radios().forEach((r) => r.closest('.dv-seg-opt').classList.toggle('is-selected', r === input));
      const d = input.dataset, unit = this.dataset.unit;
      const count = +d.count, free = +d.free;
      const q = (s) => this.querySelector(s);
      q('[data-dv-unit]').textContent = d.unitPrice;
      q('[data-dv-save]').innerHTML = d.saving ? '<span class="dv-save">' + esc(this.dataset.saveLabel + ' ' + d.saving) + '</span>' : '';
      q('[data-dv-total]').textContent = this.dataset.totalLabel + ' ' + d.total + (d.deal ? ' · ' + d.deal : '');
      let g = '';
      for (let i = 0; i < count; i++) g += '<svg class="dv-glyph' + (i >= count - free ? ' is-free' : '') + '" viewBox="0 0 18 20" aria-hidden="true">' + (GLYPHS[this.dataset.glyph] || GLYPHS.jar) + '</svg>';
      if (free) g += '<small>' + esc(this.dataset.freeLabel.replace('[n]', free).replace('[unit]', unit)) + '</small>';
      q('[data-dv-glyphs]').innerHTML = g;
      const nudge = this.querySelector('[data-dv-nudge]');
      if (nudge) nudge.hidden = this.radios().indexOf(input) !== 0;
      if (announce) this.dispatchEvent(new CustomEvent('dv:tier', { bubbles: true, detail: { index: this.radios().indexOf(input), variant: d.variant, qty: d.qty, total: d.total } }));
    }
  });

  /* ---- The buy box: one form for the box and the course sheet. Adds to the cart, then opens the cart page. ---- */
  define('dv-buy', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      if (!this.form) return;
      document.addEventListener('dv:tier', (e) => {
        const d = e.detail;
        this.form.querySelector('[data-dv-id]').value = d.variant;
        this.form.querySelector('[data-dv-qty]').value = d.qty;
        document.querySelectorAll('dv-tiers').forEach((t) => t.setIndex(d.index));
        document.querySelectorAll('[data-dv-add-total]').forEach((s) => { s.textContent = d.total; });
      });
      this.form.addEventListener('submit', (e) => this.add(e));
    }
    async add(e) {
      e.preventDefault();
      const buttons = [...document.querySelectorAll('[data-dv-add]')];
      if (buttons.some((b) => b.getAttribute('aria-busy') === 'true')) return;
      buttons.forEach((b) => { b.setAttribute('aria-busy', 'true'); b.disabled = true; });
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const r = await fetch(root + 'cart/add.js', { method: 'POST', body: new FormData(this.form), headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error('add');
        window.location.href = this.dataset.cartUrl;
      } catch (err) {
        this.form.submit();
      }
    }
  });

  /* ---- GiftRow, Details and the all-reviews button: a button opens what it controls. ---- */
  const toggle = (btn) => {
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    const body = document.getElementById(btn.getAttribute('aria-controls'));
    if (body) body.hidden = !open;
  };
  /* ---- Gift sheets: any [data-dv-gifts-open] (a gift row, the campaign bar) opens its dialog; the close button or a tap
     on the backdrop shuts it. Delegated, so rows the cart re-renders keep working. ---- */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-dv-gifts-open]');
    if (open) {
      const d = document.getElementById(open.getAttribute('aria-controls'));
      if (d && d.showModal && !d.open) { e.preventDefault(); d.showModal(); }
      return;
    }
    const sheet = e.target.closest('dialog.dv-gift-sheet');
    if (sheet && (e.target === sheet || e.target.closest('[data-dv-close-sheet]'))) sheet.close();
  });
  define('dv-details', class extends HTMLElement { connectedCallback() { this.addEventListener('click', (e) => { const b = e.target.closest('.dv-details-q'); if (b && this.contains(b)) toggle(b); }); } });
  define('dv-compare', class extends HTMLElement { connectedCallback() { const r = this.querySelector('input[type=range]'); if (r) r.addEventListener('input', () => this.style.setProperty('--x', r.value + '%')); } });
  define('dv-reveal', class extends HTMLElement { connectedCallback() { const b = this.querySelector('button[aria-controls]'); if (b) b.addEventListener('click', () => toggle(b)); } });


  /* ---- Gallery: one frame, the photos cross-fade (design system: images cross-fade, nothing slides). A photo loads when
     it is shown or is next; thumbnails, a swipe and the arrow keys change it. ---- */
  define('dv-gallery', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.dvg-slide')];
      this.thumbs = [...this.querySelectorAll('.dvg-thumb')];
      this.cur = 0;
      if (this.slides.length < 2) return;
      const stage = this.querySelector('.dvg-stage');
      this.thumbs.forEach((t, i) => {
        t.addEventListener('click', () => this.go(i));
        t.addEventListener('pointerenter', () => this.load(i));
      });
      stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.cur + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.cur - 1); }
      });
      // A horizontal swipe changes the photo; a vertical one scrolls the page (touch-action: pan-y on the stage).
      let x0 = null, y0 = 0;
      stage.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; this.load(this.cur + 1); this.load(this.cur - 1); });
      stage.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.cur + (dx < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', () => { x0 = null; });
    }
    load(i) {
      const img = this.slides[i] && this.slides[i].querySelector('img[data-src]');
      if (!img) return;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src'); img.removeAttribute('data-srcset');
    }
    go(i) {
      const n = this.slides.length;
      i = (i + n) % n;
      if (i === this.cur) return;
      this.load(i); this.load((i + 1) % n);
      this.slides.forEach((s, j) => { s.classList.toggle('is-active', j === i); s.setAttribute('aria-hidden', j === i ? 'false' : 'true'); });
      this.thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      const th = this.thumbs[i];
      if (th) th.parentElement.scrollTo({ left: th.offsetLeft - th.parentElement.clientWidth / 2 + th.clientWidth / 2, behavior: reduced() ? 'auto' : 'smooth' });
      this.cur = i;
    }
  });

  /* ---- StickyBuyBar: whenever the buy box's add button is off screen, above or below (so phones see a button on
     landing; Jamie, 20 Oct 2026), and never over the footer. ---- */
  define('dv-buybar', class extends HTMLElement {
    connectedCallback() {
      const add = document.querySelector('.dvp-form [data-dv-add]'), foot = document.querySelector('.dv-footer');
      const sheet = document.querySelector('[data-dv-sheet]');
      let addGone = false, footIn = false;
      const update = () => { const show = addGone && !footIn; this.hidden = !show; document.body.classList.toggle('dv-buybar-on', show); };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.target === add) addGone = !en.isIntersecting;
          else footIn = en.isIntersecting;
        });
        update();
      });
      if (add) io.observe(add);
      if (foot) io.observe(foot);
      const open = this.querySelector('[data-dv-open-sheet]');
      if (sheet && open) {
        open.addEventListener('click', () => sheet.showModal());
        sheet.querySelector('[data-dv-close-sheet]').addEventListener('click', () => sheet.close());
        sheet.addEventListener('click', (e) => { if (e.target === sheet) sheet.close(); });
      }
    }
  });

  /* ---- CartTimer: the 10-minute reservation, started once per cart (kept with the cart token) and never restarted, with
     the true dispatch line under it. At zero it claims nothing: it becomes the dispatch line, which ticks in the last
     six hours before the noon cut-off on a dispatch day. ---- */
  const holdKey = (token) => 'dv-hold:' + token;
  define('dv-cart-timer', class extends HTMLElement {
    connectedCallback() {
      const cart = this.closest('dv-cart');
      const token = cart ? cart.dataset.token : '';
      let started = Date.now();
      try {
        const kept = parseInt(localStorage.getItem(holdKey(token)), 10);
        if (kept > 0) started = kept; else localStorage.setItem(holdKey(token), String(started));
      } catch (e) {}
      this.started = started;
      this.holidays = (this.dataset.holidays || '').split(',').map((x) => x.trim()).filter(Boolean);
      this.words = words(this);
      this.tick();
    }
    disconnectedCallback() { clearTimeout(this.timer); }
    tick() {
      const now = Date.now(), pad = (n) => (n < 10 ? '0' : '') + n;
      const clock = (ms, hours) => { const t = Math.max(0, Math.floor(ms / 1000)); return (hours ? pad(Math.floor(t / 3600)) + ':' : '') + pad(Math.floor(t / 60) % 60) + ':' + pad(t % 60); };
      const holdMs = (parseFloat(this.dataset.hold) || 0) * 6e4;
      const holdLeft = this.started + holdMs - now;
      const holding = holdMs > 0 && holdLeft > 0;
      const x = dispatchInfo({ holidays: this.holidays }, now, this.words);
      const left = x.cutoffAt - now;
      const cut = !holding && x.today && left > 0 && left <= 6 * 36e5;
      const head = this.querySelector('[data-dv-head]'), sub = this.querySelector('[data-dv-sub]');
      if (holding) {
        head.innerHTML = esc(this.dataset.holdText) + ' <time class="dv-timer-clock">' + clock(holdLeft, false) + '</time>';
        sub.textContent = x.send + ' · ' + x.when;
      } else if (cut) {
        const parts = (this.dataset.cutText || '').split('[clock]');
        head.innerHTML = esc(parts[0] || '') + '<time class="dv-timer-clock">' + clock(left, true) + '</time>' + esc(parts[1] || '');
        sub.textContent = x.when;
      } else {
        head.textContent = x.head;
        sub.textContent = x.when;
      }
      this.classList.toggle('is-hold', holding);
      this.classList.toggle('is-live', holding || cut);
      const icon = this.querySelector('svg');
      if (icon) icon.innerHTML = holding || cut ? '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5M9.5 2.5h5"/>'
        : '<path d="M2.5 6.5h11v9h-11zM13.5 9.5h4l3 3v3h-7"/><circle cx="6.5" cy="17.5" r="1.8"/><circle cx="16.5" cy="17.5" r="1.8"/>';
      if (holding || cut) this.timer = setTimeout(() => this.tick(), 1000);
    }
  });

  /* ---- The cart: quantities, removing and discount codes go through the cart API, then the
     section is asked for again, so every figure on screen is Shopify's. ---- */
  define('dv-cart', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.matches('.dv-code-input')) { e.preventDefault(); this.applyCode(); } });
      this.watchBar();
      if (+this.dataset.count === 0) { try { Object.keys(localStorage).filter((k) => k.indexOf('dv-hold:') === 0).forEach((k) => localStorage.removeItem(k)); } catch (e) {} }
    }
    root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
    async post(path, body) {
      // The cart section comes back with the change itself (Section Rendering API), so the refresh needs no second request.
      body = Object.assign({}, body, { sections: this.dataset.section, sections_url: this.dataset.cartUrl });
      const r = await fetch(this.root() + path.replace(/^\//, ''), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.description || data.message || 'error');
      return data;
    }
    onClick(e) {
      // One pack short of a free one: add one more of the cheapest deal pack, which the discounts make free.
      const free = e.target.closest('[data-dv-add-free]');
      if (free) { e.preventDefault(); return this.run(null, () => this.post('/cart/add.js', { items: [{ id: +free.dataset.dvAddFree, quantity: 1 }] })); }
      const q = e.target.closest('[data-dv-qty]');
      if (q) { e.preventDefault(); const line = q.closest('.dv-cline'); return this.run(line, () => this.post('/cart/change.js', { id: line.dataset.key, quantity: Math.max(0, +q.dataset.dvQty) })); }
      if (e.target.closest('[data-dv-code-apply]')) { e.preventDefault(); return this.applyCode(); }
      const rm = e.target.closest('[data-dv-code-remove]');
      if (rm) { e.preventDefault(); const keep = this.codes().filter((c) => c !== rm.dataset.dvCodeRemove); return this.run(null, () => this.post('/cart/update.js', { discount: keep.join(',') })); }
    }
    codes() { return [...this.querySelectorAll('.dv-code-chip')].map((n) => n.dataset.code).filter(Boolean); }
    async applyCode() {
      const input = this.querySelector('.dv-code-input');
      const code = input && input.value.trim();
      if (!code) return;
      await this.run(null, () => this.post('/cart/update.js', { discount: this.codes().concat(code).join(',') }));
      if (!this.codes().some((c) => c.toLowerCase() === code.toLowerCase())) {
        const err = this.querySelector('[data-dv-error]'), box = this.querySelector('.dv-code');
        if (err) { err.textContent = this.dataset.codeError || ''; err.hidden = !this.dataset.codeError; }
        if (box) box.open = true;
      }
    }
    async run(line, task) {
      if (this.busy) return;
      this.busy = true;
      if (line) { line.classList.add('is-busy'); line.setAttribute('aria-busy', 'true'); }
      try { const data = await task(); await this.refresh(data && data.sections && data.sections[this.dataset.section]); }
      catch (err) { if (line) { line.classList.remove('is-busy'); line.removeAttribute('aria-busy'); } }
      this.busy = false;
    }
    async refresh(html) {
      if (!html) {
        const url = this.dataset.cartUrl + '?section_id=' + encodeURIComponent(this.dataset.section);
        html = await (await fetch(url, { headers: { Accept: 'text/html' } })).text();
      }
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('dv-cart');
      if (next) { this.innerHTML = next.innerHTML; this.dataset.count = next.dataset.count; this.dataset.token = next.dataset.token; }
      // Split tests: a <details data-dv-open-test="key:variant"> stays open for that variant after the refresh (cart1 B's
      // code field; on first paint an inline script opens it).
      this.querySelectorAll('details[data-dv-open-test]').forEach((d) => {
        const t = d.dataset.dvOpenTest.split(':');
        if (document.documentElement.getAttribute('data-test-' + t[0]) === t[1]) d.open = true;
      });
      const n = +this.dataset.count || 0;
      document.querySelectorAll('.dv-header-bag').forEach((bag) => {
        let c = bag.querySelector('.dv-count');
        if (n && !c) { c = document.createElement('span'); c.className = 'dv-count'; c.setAttribute('aria-hidden', 'true'); bag.append(c); }
        if (c) { if (n) c.textContent = n; else c.remove(); }
      });
      this.watchBar();
    }
    watchBar() {
      const bar = this.querySelector('[data-dv-cart-bar]');
      if (!bar) return;
      // The bar (total + checkout) stays on screen the whole time on phones, and the in-page button stays too: split
      // test cart2's version B, the cart since Jamie ended the test (3 Nov 2026). divyne-ds.css shows it from the first
      // paint and hides it on desktop, where the summary sits beside the lines. (cart1's B, when it runs, also hides the
      // in-page button there.)
      const phone = window.matchMedia('(max-width: 989px)').matches;
      bar.hidden = !phone;
      document.body.classList.toggle('dv-cartbar-on', phone);
    }
  });

  /* ---- SiteHeader: hides as she scrolls down, comes back when she scrolls up; opens the MenuDrawer. ---- */
  define('dv-header', class extends HTMLElement {
    connectedCallback() {
      let last = 0;
      document.addEventListener('scroll', (e) => {
        const t = e.target;
        const doc = t === document || t === document.documentElement;
        if (!doc && !(t.classList && t.classList.contains('page-wrapper'))) return;
        const y = doc ? window.scrollY : t.scrollTop;
        if (Math.abs(y - last) < 6) return;
        this.classList.toggle('is-hidden', y > last && y > 120);
        last = y;
      }, { capture: true, passive: true });
      const btn = this.querySelector('[data-dv-menu-open]');
      const dlg = btn && document.getElementById(btn.getAttribute('aria-controls'));
      if (!dlg) return;
      btn.addEventListener('click', () => { dlg.showModal(); btn.setAttribute('aria-expanded', 'true'); });
      dlg.addEventListener('close', () => btn.setAttribute('aria-expanded', 'false'));
      dlg.querySelector('[data-dv-menu-close]').addEventListener('click', () => dlg.close());
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    }
  });
})();
